import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../ui/ToastProvider'
import { formatApiDateTimeDisplay } from '../lib/date'
import { formatKoreanPhoneInput } from '../lib/crmInputFormat'
import { PDF_MAPPING_FONT_OPTIONS } from '../lib/pdfMappingFontOptions'
import { PdfTemplateMapper } from './Customers'

const FIELD_PRESETS = [
  { bindKey: 'plannerIntroText', label: '소개 문구', width: 0.42, height: 0.045, fontSize: 26, align: 'center' },
  { bindKey: 'plannerQualifications', label: '자격사항', width: 0.42, height: 0.04, fontSize: 12, align: 'left' },
  { bindKey: 'plannerConsultingFields', label: '상담분야', width: 0.42, height: 0.04, fontSize: 12, align: 'left' },
  { bindKey: 'plannerLine', label: '소속/이름/연락처', width: 0.72, height: 0.03, fontSize: 12, align: 'center' },
  { bindKey: 'plannerNameWithJobTitle', label: '이름+직함', width: 0.28, height: 0.035, fontSize: 16, align: 'center' },
  { bindKey: 'plannerName', label: '설계사명', width: 0.22, height: 0.035, fontSize: 16, align: 'center' },
  { bindKey: 'plannerNameQuoted', label: '이름강조', width: 0.24, height: 0.035, fontSize: 18, align: 'center' },
  { bindKey: 'plannerHeaderIntro', label: '헤더소개', width: 0.58, height: 0.04, fontSize: 18, align: 'center' },
  { bindKey: 'plannerPhone', label: '연락처', width: 0.24, height: 0.03, fontSize: 12, align: 'center' },
  { bindKey: 'plannerOrgName', label: '소속', width: 0.28, height: 0.03, fontSize: 12, align: 'center' },
  { bindKey: 'plannerJobTitle', label: '직함', width: 0.16, height: 0.03, fontSize: 12, align: 'center' },
  { bindKey: 'customerName', label: '고객명', width: 0.18, height: 0.025, fontSize: 10, align: 'center' },
  { bindKey: 'deliveryDate', label: '전달일', width: 0.18, height: 0.025, fontSize: 10, align: 'center' },
  { bindKey: 'policyNumber', label: '증권번호', width: 0.24, height: 0.025, fontSize: 10, align: 'center' },
  { bindKey: 'userComment', label: '전달 코멘트', width: 0.52, height: 0.08, fontSize: 11, align: 'left' },
  { bindKey: 'reportImage', label: '리포트용 고정 이미지', width: 0.72, height: 0.26, fontSize: 12, align: 'center', type: 'image', valueType: 'STATIC', value: '' },
  { bindKey: 'businessCardImage', label: '명함용 고정 이미지', width: 0.46, height: 0.16, fontSize: 12, align: 'center', type: 'image', valueType: 'STATIC', value: '' },
  { bindKey: 'reportPhoto', label: '리포트용 설계사 사진', width: 0.28, height: 0.24, fontSize: 12, align: 'center', type: 'image' },
  { bindKey: 'businessCardPhoto', label: '명함용 설계사 사진', width: 0.22, height: 0.16, fontSize: 12, align: 'center', type: 'image' },
  { bindKey: 'printedAt', label: '출력일', width: 0.18, height: 0.025, fontSize: 9, align: 'center' },
  { bindKey: 'staticText', label: '직접 입력', width: 0.28, height: 0.035, fontSize: 12, align: 'left', valueType: 'STATIC', value: '문구 입력' },
]

const FIELD_LABELS = FIELD_PRESETS.reduce((acc, item) => ({ ...acc, [item.bindKey]: item.label }), {
  printedAt: '출력일',
})


const DYNAMIC_USER_PHOTO_BIND_KEYS = new Set(['reportPhoto', 'businessCardPhoto'])

const BASE_SECTION_LABELS = [
  '표지',
  '설계사 소개',
  '연락 필요 상황',
  '보험금 청구 필요서류',
  '보험금 지급절차',
  '보험용어',
  '보험사별 연락처',
]

const PROFILE_FIELD_PRESET = [
  { bindKey: 'plannerName', pageIndex: 1, sectionKey: 'section_2', x: 0.16, y: 0.23, w: 0.32, h: 0.05, fontSize: 22, align: 'left' },
  { bindKey: 'plannerOrgName', pageIndex: 1, sectionKey: 'section_2', x: 0.16, y: 0.3, w: 0.34, h: 0.03, fontSize: 11, align: 'left' },
  { bindKey: 'plannerPhone', pageIndex: 1, sectionKey: 'section_2', x: 0.58, y: 0.3, w: 0.28, h: 0.03, fontSize: 11, align: 'left' },
  { bindKey: 'plannerQualifications', pageIndex: 1, sectionKey: 'section_2', x: 0.16, y: 0.48, w: 0.72, h: 0.05, fontSize: 11, align: 'left' },
  { bindKey: 'plannerConsultingFields', pageIndex: 1, sectionKey: 'section_2', x: 0.16, y: 0.58, w: 0.72, h: 0.05, fontSize: 11, align: 'left' },
  { bindKey: 'plannerIntroText', pageIndex: 1, sectionKey: 'section_2', x: 0.16, y: 0.68, w: 0.72, h: 0.12, fontSize: 12, align: 'left' },
]

function makeSequentialSections(labels) {
  return labels.map((label, index) => ({
    key: `section_${index + 1}`,
    label,
    pageStart: index + 1,
    pageEnd: index + 1,
    defaultIncluded: true,
  }))
}

function makeField(item, index, sections) {
  const section = sections.find((candidate) => candidate.key === item.sectionKey)
  return {
    key: `field_${index + 1}`,
    bindKey: item.bindKey,
    pageIndex: item.pageIndex ?? 0,
    x: item.x ?? 0.1,
    y: item.y ?? 0.1,
    w: item.w ?? 0.24,
    h: item.h ?? 0.03,
    fontSize: item.fontSize ?? 12,
    align: item.align || 'left',
    type: item.type || 'text',
    sectionKey: item.sectionKey || '',
    sectionLabel: section?.label || '',
    valueType: item.valueType || '',
    value: item.value || '',
    fontKey: item.fontKey || '',
    coverBackground: Boolean(item.coverBackground),
    backgroundColor: item.backgroundColor || '#ffffff',
    textColor: item.textColor || '#111827',
    fitFont: item.fitFont !== false,
  }
}

const POLICY_DELIVERY_LAYOUT_PRESETS = [
  {
    label: '공양식 7페이지형',
    description: '기본 공통 섹션에 설계사 정보와 코멘트를 얹는 구성',
    sections: makeSequentialSections(BASE_SECTION_LABELS),
    fields: [
      { bindKey: 'plannerIntroText', pageIndex: 0, sectionKey: 'section_1', x: 0.12, y: 0.3, w: 0.76, h: 0.08, fontSize: 24, align: 'center' },
      { bindKey: 'plannerNameWithJobTitle', pageIndex: 0, sectionKey: 'section_1', x: 0.25, y: 0.62, w: 0.5, h: 0.045, fontSize: 18, align: 'center' },
      { bindKey: 'plannerPhone', pageIndex: 0, sectionKey: 'section_1', x: 0.32, y: 0.69, w: 0.36, h: 0.03, fontSize: 12, align: 'center' },
      { bindKey: 'userComment', pageIndex: 0, sectionKey: 'section_1', x: 0.16, y: 0.75, w: 0.72, h: 0.08, fontSize: 11, align: 'center' },
      { bindKey: 'customerName', pageIndex: 0, sectionKey: 'section_1', x: 0.15, y: 0.88, w: 0.18, h: 0.025, fontSize: 10, align: 'center' },
      { bindKey: 'deliveryDate', pageIndex: 0, sectionKey: 'section_1', x: 0.38, y: 0.88, w: 0.18, h: 0.025, fontSize: 10, align: 'center' },
      { bindKey: 'policyNumber', pageIndex: 0, sectionKey: 'section_1', x: 0.61, y: 0.88, w: 0.24, h: 0.025, fontSize: 10, align: 'center' },
      ...PROFILE_FIELD_PRESET,
      { bindKey: 'plannerLine', pageIndex: 6, sectionKey: 'section_7', x: 0.16, y: 0.88, w: 0.72, h: 0.03, fontSize: 10, align: 'center' },
    ],
  },
  {
    label: '프로필 강조 8페이지형',
    description: '표지 하단 서명과 소개 페이지를 크게 쓰는 구성',
    sections: makeSequentialSections([...BASE_SECTION_LABELS, '증권 확인 체크리스트']),
    fields: [
      { bindKey: 'plannerIntroText', pageIndex: 0, sectionKey: 'section_1', x: 0.1, y: 0.26, w: 0.8, h: 0.09, fontSize: 25, align: 'center' },
      { bindKey: 'plannerNameWithJobTitle', pageIndex: 0, sectionKey: 'section_1', x: 0.08, y: 0.77, w: 0.42, h: 0.045, fontSize: 17, align: 'left' },
      { bindKey: 'plannerOrgName', pageIndex: 0, sectionKey: 'section_1', x: 0.08, y: 0.83, w: 0.34, h: 0.03, fontSize: 10, align: 'left' },
      { bindKey: 'plannerPhone', pageIndex: 0, sectionKey: 'section_1', x: 0.58, y: 0.83, w: 0.3, h: 0.03, fontSize: 10, align: 'right' },
      ...PROFILE_FIELD_PRESET.map((field) => ({ ...field, x: field.x + 0.02, w: Math.max(0.2, field.w - 0.04) })),
      { bindKey: 'userComment', pageIndex: 7, sectionKey: 'section_8', x: 0.14, y: 0.68, w: 0.72, h: 0.1, fontSize: 12, align: 'left' },
      { bindKey: 'plannerLine', pageIndex: 7, sectionKey: 'section_8', x: 0.16, y: 0.86, w: 0.72, h: 0.03, fontSize: 10, align: 'center' },
    ],
  },
  {
    label: '체크리스트 확장 10페이지형',
    description: '전달 체크리스트와 유의사항까지 나눠 보여주는 구성',
    sections: makeSequentialSections([
      '표지',
      '설계사 소개',
      '증권 전달 체크리스트',
      '연락 필요 상황',
      '보험사별 연락처',
      '보험용어',
      '보험금 청구와 지급 흐름',
      '보험금 청구 필요서류',
      '놓치기 쉬운 유의사항',
      '보관과 다음 점검',
    ]),
    fields: [
      { bindKey: 'plannerNameWithJobTitle', pageIndex: 0, sectionKey: 'section_1', x: 0.12, y: 0.41, w: 0.5, h: 0.055, fontSize: 22, align: 'left' },
      { bindKey: 'plannerPhone', pageIndex: 0, sectionKey: 'section_1', x: 0.12, y: 0.5, w: 0.32, h: 0.03, fontSize: 11, align: 'left' },
      { bindKey: 'plannerConsultingFields', pageIndex: 0, sectionKey: 'section_1', x: 0.52, y: 0.5, w: 0.36, h: 0.04, fontSize: 10, align: 'left' },
      ...PROFILE_FIELD_PRESET,
      { bindKey: 'userComment', pageIndex: 9, sectionKey: 'section_10', x: 0.12, y: 0.38, w: 0.76, h: 0.1, fontSize: 12, align: 'left' },
      { bindKey: 'plannerLine', pageIndex: 9, sectionKey: 'section_10', x: 0.16, y: 0.76, w: 0.72, h: 0.03, fontSize: 10, align: 'center' },
      { bindKey: 'printedAt', pageIndex: 9, sectionKey: 'section_10', x: 0.38, y: 0.86, w: 0.24, h: 0.025, fontSize: 9, align: 'center' },
    ],
  },
]

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res.data
}

function parseFields(record) {
  if (!record) return []
  if (Array.isArray(record.fields)) return record.fields
  if (!record.fieldsJson) return []
  try {
    const parsed = JSON.parse(record.fieldsJson)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function parseSections(record) {
  if (!record) return []
  if (Array.isArray(record.sections)) return record.sections
  if (!record.sectionsJson) return []
  try {
    const parsed = JSON.parse(record.sectionsJson)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function nextFieldKey(fields) {
  const max = (fields || []).reduce((acc, field) => {
    const match = String(field?.key || '').match(/^field_(\d+)$/)
    return match ? Math.max(acc, Number(match[1])) : acc
  }, 0)
  return `field_${max + 1}`
}

function nextSectionKey(sections) {
  const max = (sections || []).reduce((acc, section) => {
    const match = String(section?.key || '').match(/^section_(\d+)$/)
    return match ? Math.max(acc, Number(match[1])) : acc
  }, 0)
  return `section_${max + 1}`
}

function fallbackNameFromDisposition(header, fallback) {
  const value = String(header || '')
  const star = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (star?.[1]) {
    try { return decodeURIComponent(star[1]) } catch { return star[1] }
  }
  const plain = value.match(/filename="([^"]+)"/i)
  return plain?.[1] || fallback
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

const emptyForm = {
  id: null,
  title: '',
  description: '',
  templateFileId: '',
  active: true,
  sortOrder: 0,
}

export default function PolicyDeliveryBookTemplatesPage() {
  const { user } = useAuth()
  const { show } = useToast()
  const [templates, setTemplates] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [fields, setFields] = useState([])
  const [sections, setSections] = useState([])
  const [selectedFieldKey, setSelectedFieldKey] = useState(null)
  const [pdfPageIndex, setPdfPageIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadingImageFieldKey, setUploadingImageFieldKey] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewBusy, setPreviewBusy] = useState(false)
  const didAutoSelectInitialTemplateRef = useRef(false)
  const [openPanels, setOpenPanels] = useState({
    layoutPresets: true,
    fieldPresets: true,
    sections: true,
  })

  const selectedField = useMemo(
    () => fields.find((field) => field.key === selectedFieldKey) || null,
    [fields, selectedFieldKey],
  )

  const togglePanel = (key) => {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const previewValues = useMemo(() => {
    const name = user?.name || '홍길동'
    const phone = formatKoreanPhoneInput(user?.phoneNumber || '01012345678') || '010-1234-5678'
    const org = user?.affiliationDisplayName || user?.orgName || '인카금융서비스'
    const jobTitle = user?.jobTitle || 'FA'
    return {
      plannerName: name,
      plannerPhone: phone,
      plannerOrgName: org,
      plannerJobTitle: jobTitle,
      plannerLine: `${org} ${name} ${jobTitle} : ${phone}`,
      plannerNameQuoted: `"${name}"`,
      plannerHeaderIntro: `${name} 컨설턴트 사용설명서`,
      plannerIntroText: `보험설계사 ${name} 입니다.`,
      plannerQualifications: '생명보험·손해보험 모집자격 / 변액보험 판매자격',
      plannerConsultingFields: '보험 리모델링, 실손·건강보험, 가족 보장분석',
      customerName: '고객명',
      deliveryDate: new Date().toISOString().slice(0, 10),
      policyNumber: 'POLICY-2026-0001',
      userComment: '증권 내용 확인 후 편하게 연락 주세요.',
      staticText: '문구 입력',
    }
  }, [user])

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const payload = unwrap(await api.get('/policy-delivery-books/operator/templates'))
      const rows = Array.isArray(payload) ? payload : []
      setTemplates(rows)
      const shouldAutoSelect = !didAutoSelectInitialTemplateRef.current && !form.id && rows.length > 0
      didAutoSelectInitialTemplateRef.current = true
      if (shouldAutoSelect) {
        selectTemplate(rows[0])
      }
    } catch {
      show('증권전달북 템플릿 목록을 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, show])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  useEffect(() => () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const selectTemplate = (template) => {
    setForm({
      id: template?.id || null,
      title: template?.title || '',
      description: template?.description || '',
      templateFileId: template?.templateFileId || '',
      active: template?.active !== false,
      sortOrder: Number(template?.sortOrder ?? 0),
    })
    const nextFields = parseFields(template)
    const nextSections = parseSections(template)
    setFields(nextFields)
    setSections(nextSections)
    setSelectedFieldKey(nextFields[0]?.key || null)
    setPdfPageIndex(0)
    clearPreview()
  }

  const clearPreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
    setPreviewUrl('')
  }

  const startNew = () => {
    setForm(emptyForm)
    setFields([])
    setSections([])
    setSelectedFieldKey(null)
    setPdfPageIndex(0)
    clearPreview()
  }

  const applyLayoutPreset = (preset) => {
    if (!preset) return
    if ((fields.length > 0 || sections.length > 0) && !window.confirm('기존 필드와 섹션을 이 프리셋으로 바꿀까요?')) {
      return
    }
    const nextSections = (preset.sections || []).map((section) => ({ ...section }))
    const nextFields = (preset.fields || []).map((field, index) => makeField(field, index, nextSections))
    setSections(nextSections)
    setFields(nextFields)
    setSelectedFieldKey(nextFields[0]?.key || null)
    setPdfPageIndex(0)
    clearPreview()
    show(`${preset.label} 프리셋을 적용했습니다.`, 'success')
  }

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const addField = (preset) => {
    const key = nextFieldKey(fields)
    const next = {
      key,
      bindKey: preset.bindKey,
      pageIndex: pdfPageIndex,
      x: 0.1,
      y: 0.1,
      w: preset.width || 0.24,
      h: preset.height || 0.03,
      fontSize: preset.fontSize || 12,
      align: preset.align || 'left',
      type: preset.type || 'text',
      sectionKey: sections[0]?.key || '',
      sectionLabel: sections[0]?.label || '',
      valueType: preset.valueType || '',
      value: preset.value || '',
      fontKey: '',
      coverBackground: false,
      backgroundColor: '#ffffff',
      textColor: '#111827',
      fitFont: true,
    }
    setFields((prev) => [...prev, next])
    setSelectedFieldKey(key)
  }

  const updateField = (key, patch) => {
    setFields((prev) => prev.map((field) => (field.key === key ? { ...field, ...patch } : field)))
  }

  const addSection = () => {
    const key = nextSectionKey(sections)
    const next = {
      key,
      label: `섹션 ${sections.length + 1}`,
      pageStart: 1,
      pageEnd: 1,
      defaultIncluded: true,
    }
    setSections((prev) => [...prev, next])
  }

  const updateSection = (key, patch) => {
    setSections((prev) => prev.map((section) => (section.key === key ? { ...section, ...patch } : section)))
    if (patch.label != null) {
      setFields((prev) => prev.map((field) => (field.sectionKey === key ? { ...field, sectionLabel: patch.label } : field)))
    }
  }

  const deleteSection = (key) => {
    setSections((prev) => prev.filter((section) => section.key !== key))
    setFields((prev) => prev.map((field) => (field.sectionKey === key ? { ...field, sectionKey: '', sectionLabel: '' } : field)))
  }

  const deleteField = (key) => {
    setFields((prev) => prev.filter((field) => field.key !== key))
    if (selectedFieldKey === key) {
      const next = fields.find((field) => field.key !== key)
      setSelectedFieldKey(next?.key || null)
    }
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type && file.type !== 'application/pdf') {
      show('PDF 파일만 업로드할 수 있습니다.', 'error')
      e.target.value = ''
      return
    }
    setUploading(true)
    try {
      const body = new FormData()
      body.append('file', file)
      const payload = unwrap(await api.post('/files/upload/policy-delivery-book-template', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }))
      const fileId = payload?.fileId || payload?.id
      if (!fileId) throw new Error('fileId missing')
      updateForm('templateFileId', fileId)
      clearPreview()
      show('템플릿 PDF가 업로드되었습니다.', 'success')
    } catch {
      show('템플릿 PDF 업로드에 실패했습니다.', 'error')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const handleImageUpload = async (fieldKey, file) => {
    if (!fieldKey || !file) return
    if (file.type && !String(file.type).startsWith('image/')) {
      show('이미지 파일만 업로드할 수 있습니다.', 'error')
      return
    }
    setUploadingImageFieldKey(fieldKey)
    try {
      const body = new FormData()
      body.append('file', file)
      const payload = unwrap(await api.post('/files/upload/policy-delivery-book-image', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }))
      const fileId = payload?.fileId || payload?.id
      if (!fileId) throw new Error('fileId missing')
      updateField(fieldKey, { type: 'image', valueType: 'STATIC', value: fileId })
      clearPreview()
      show('리포트 이미지가 업로드되었습니다.', 'success')
    } catch {
      show('리포트 이미지 업로드에 실패했습니다.', 'error')
    } finally {
      setUploadingImageFieldKey(null)
    }
  }

  const saveTemplate = async () => {
    if (!form.title.trim()) {
      show('템플릿명을 입력해 주세요.', 'error')
      return
    }
    if (!form.templateFileId.trim()) {
      show('템플릿 PDF를 업로드해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        templateFileId: form.templateFileId.trim(),
        active: Boolean(form.active),
        sortOrder: Number(form.sortOrder || 0),
        fields,
        sections,
      }
      const payload = form.id
        ? unwrap(await api.put(`/policy-delivery-books/operator/templates/${form.id}`, body))
        : unwrap(await api.post('/policy-delivery-books/operator/templates', body))
      show('증권전달북 템플릿을 저장했습니다.', 'success')
      await loadTemplates()
      if (payload?.id) selectTemplate(payload)
    } catch {
      show('증권전달북 템플릿 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const deleteTemplate = async () => {
    if (!form.id) return
    if (!window.confirm('선택한 템플릿을 삭제할까요?')) return
    try {
      await api.delete(`/policy-delivery-books/operator/templates/${form.id}`)
      show('증권전달북 템플릿을 삭제했습니다.', 'success')
      startNew()
      await loadTemplates()
    } catch {
      show('증권전달북 템플릿 삭제에 실패했습니다.', 'error')
    }
  }

  const previewTemplate = async () => {
    if (!form.id) {
      show('저장된 템플릿만 미리볼 수 있습니다.', 'info')
      return
    }
    setPreviewBusy(true)
    try {
      const res = await api.get(`/policy-delivery-books/operator/templates/${form.id}/preview`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      clearPreview()
      setPreviewUrl(window.URL.createObjectURL(blob))
    } catch {
      show('미리보기 PDF 생성에 실패했습니다.', 'error')
    } finally {
      setPreviewBusy(false)
    }
  }

  const downloadOriginal = async () => {
    if (!form.templateFileId) return
    try {
      const res = await api.get(`/files/${encodeURIComponent(form.templateFileId)}/download`, { responseType: 'blob' })
      const filename = fallbackNameFromDisposition(res?.headers?.['content-disposition'], `${form.title || 'template'}.pdf`)
      downloadBlob(new Blob([res.data], { type: res?.headers?.['content-type'] || 'application/pdf' }), filename)
    } catch {
      show('원본 PDF 다운로드에 실패했습니다.', 'error')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold text-slate-900">증권전달북</h1>
          <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
        </div>
        <button
          type="button"
          onClick={startNew}
          className="rounded bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          새 템플릿
        </button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm xl:sticky xl:top-24 xl:self-start">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-900">템플릿 목록</div>
            <div className="text-xs text-slate-500">{loading ? '불러오는 중' : `${templates.length}개`}</div>
          </div>
          <div className="space-y-2">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => selectTemplate(template)}
                className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                  form.id === template.id ? 'border-sgez-primary bg-sky-50 ring-1 ring-sgez-primary/20' : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-sm font-semibold text-slate-900">{template.title}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${template.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {template.active ? '사용' : '중지'}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{formatApiDateTimeDisplay(template.updatedAtDisplay, template.updatedAt) || '-'}</div>
              </button>
            ))}
            {!templates.length && (
              <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
                등록된 템플릿이 없습니다.
              </div>
            )}
          </div>
        </aside>

        <main className="min-w-0 space-y-4">
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px_120px]">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">템플릿명</span>
                <input
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                  value={form.title}
                  onChange={(e) => updateForm('title', e.target.value)}
                  placeholder="예: 퍼플 전달북"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">정렬순서</span>
                <input
                  type="number"
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                  value={form.sortOrder}
                  onChange={(e) => updateForm('sortOrder', e.target.value)}
                />
              </label>
              <label className="flex items-end gap-2 pb-2 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.active)}
                  onChange={(e) => updateForm('active', e.target.checked)}
                />
                사용자 노출
              </label>
            </div>

            <label className="mt-3 block">
              <span className="text-xs font-semibold text-slate-700">설명</span>
              <input
                className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                value={form.description}
                onChange={(e) => updateForm('description', e.target.value)}
                placeholder="사용자가 구분할 수 있는 짧은 설명"
              />
            </label>

            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-end">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">PDF 파일</span>
                <input
                  className="mt-1 w-full rounded border border-slate-200 px-3 py-2 font-mono text-xs outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
                  value={form.templateFileId}
                  onChange={(e) => updateForm('templateFileId', e.target.value)}
                  placeholder="파일 업로드 후 자동 입력"
                />
              </label>
              <label className="inline-flex cursor-pointer items-center justify-center rounded border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                {uploading ? '업로드 중' : 'PDF 업로드'}
                <input type="file" accept="application/pdf" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
              <button
                type="button"
                onClick={downloadOriginal}
                disabled={!form.templateFileId}
                className="rounded border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                원본 다운로드
              </button>
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
            <div className="space-y-4">
              <CollapsiblePanel
                title="레이아웃 프리셋"
                open={openPanels.layoutPresets}
                onToggle={() => togglePanel('layoutPresets')}
              >
                <div className="space-y-2">
                  {POLICY_DELIVERY_LAYOUT_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => applyLayoutPreset(preset)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-left hover:bg-slate-50"
                    >
                      <div className="text-sm font-semibold text-slate-900">{preset.label}</div>
                      <div className="mt-0.5 text-xs text-slate-500">{preset.description}</div>
                    </button>
                  ))}
                </div>
              </CollapsiblePanel>
              <CollapsiblePanel
                title="필드 추가"
                open={openPanels.fieldPresets}
                onToggle={() => togglePanel('fieldPresets')}
              >
                <div className="flex flex-wrap gap-2">
                  {FIELD_PRESETS.map((preset) => (
                    <button
                      key={preset.bindKey}
                      type="button"
                      onClick={() => addField(preset)}
                      className="rounded border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </CollapsiblePanel>
              <CollapsiblePanel
                title="출력 섹션"
                open={openPanels.sections}
                onToggle={() => togglePanel('sections')}
                action={(
                  <button type="button" onClick={addSection} className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    섹션 추가
                  </button>
                )}
              >
                <SectionEditor sections={sections} onChange={updateSection} onDelete={deleteSection} />
              </CollapsiblePanel>
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-bold text-slate-900">필드 목록</div>
                  <div className="text-xs text-slate-500">{fields.length}개</div>
                </div>
                <div className="max-h-[360px] space-y-2 overflow-auto pr-1">
                  {fields.map((field) => (
                    <button
                      key={field.key}
                      type="button"
                      onClick={() => setSelectedFieldKey(field.key)}
                      className={`w-full rounded border px-3 py-2 text-left text-sm ${
                        selectedFieldKey === field.key ? 'border-sgez-primary bg-sky-50' : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="font-semibold text-slate-900">{FIELD_LABELS[field.bindKey] || field.bindKey || field.key}</div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        페이지 {Number(field.pageIndex ?? 0) + 1}
                        {field.sectionLabel ? ` · ${field.sectionLabel}` : ''}
                      </div>
                    </button>
                  ))}
                  {!fields.length && <div className="rounded border border-dashed border-slate-200 p-3 text-center text-sm text-slate-500">필드를 추가해 주세요.</div>}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 text-sm font-bold text-slate-900">선택 필드</div>
                {selectedField ? (
                  <FieldEditor
                    field={selectedField}
                    sections={sections}
                    onChange={(patch) => updateField(selectedField.key, patch)}
                    onDelete={() => deleteField(selectedField.key)}
                    onUploadImage={(file) => handleImageUpload(selectedField.key, file)}
                    imageUploading={uploadingImageFieldKey === selectedField.key}
                  />
                ) : (
                  <div className="text-sm text-slate-500">PDF에서 편집할 필드를 선택해 주세요.</div>
                )}
              </div>
            </div>

            <div className="min-w-0 space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                {form.templateFileId ? (
                  <PdfTemplateMapper
                    fileId={form.templateFileId}
                    fields={fields}
                    selectedKey={selectedFieldKey}
                    onSelectKey={setSelectedFieldKey}
                    onPageIndexChange={(pageIndex) => setPdfPageIndex(pageIndex)}
                    onSetBox={(key, box) => updateField(key, box)}
                    onSetBoxes={(updates) => {
                      const patches = new Map((updates || []).map((item) => [item.key, item.box || {}]))
                      setFields((prev) => prev.map((field) => (patches.has(field.key) ? { ...field, ...patches.get(field.key) } : field)))
                    }}
                    onDeleteField={deleteField}
                    previewEnabled
                    previewValues={previewValues}
                    fieldLabels={FIELD_LABELS}
                    enableBulkTools
                    showMappingOverlayLabels
                  />
                ) : (
                  <div className="rounded border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
                    템플릿 PDF를 업로드하면 좌표를 지정할 수 있습니다.
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={saveTemplate}
                  disabled={saving}
                  className="rounded bg-sgez-primary px-4 py-2 text-sm font-semibold text-white hover:bg-sgez-primary/90 disabled:opacity-50"
                >
                  {saving ? '저장 중' : '저장'}
                </button>
                <button
                  type="button"
                  onClick={previewTemplate}
                  disabled={!form.id || previewBusy}
                  className="rounded border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {previewBusy ? '생성 중' : '치환 미리보기'}
                </button>
                <button
                  type="button"
                  onClick={deleteTemplate}
                  disabled={!form.id}
                  className="rounded border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  삭제
                </button>
              </div>

              {previewUrl && (
                <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                  <iframe title="증권전달북 미리보기" src={previewUrl} className="h-[640px] w-full rounded border border-slate-200" />
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

function CollapsiblePanel({ title, open, onToggle, action, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-bold text-slate-900"
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />}
          <span className="truncate">{title}</span>
        </button>
        {action}
      </div>
      {open && <div className="mt-3">{children}</div>}
    </div>
  )
}

function SectionEditor({ sections, onChange, onDelete }) {
  if (!sections.length) {
    return <div className="rounded border border-dashed border-slate-200 p-3 text-sm text-slate-500">섹션을 추가하면 사용자가 출력할 페이지 묶음을 선택할 수 있습니다.</div>
  }
  return (
    <div className="space-y-3">
      {sections.map((section) => (
        <div key={section.key} className="rounded-lg border border-slate-200 p-3">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_72px_72px]">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">섹션명</span>
              <input
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                value={section.label || ''}
                onChange={(e) => onChange(section.key, { label: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">시작</span>
              <input
                type="number"
                min="1"
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                value={section.pageStart ?? 1}
                onChange={(e) => onChange(section.key, { pageStart: Number(e.target.value || 1) })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">끝</span>
              <input
                type="number"
                min="1"
                className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                value={section.pageEnd ?? section.pageStart ?? 1}
                onChange={(e) => onChange(section.key, { pageEnd: Number(e.target.value || 1) })}
              />
            </label>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={section.defaultIncluded !== false}
                onChange={(e) => onChange(section.key, { defaultIncluded: e.target.checked })}
              />
              기본 선택
            </label>
            <button type="button" onClick={() => onDelete(section.key)} className="text-xs font-semibold text-red-600 hover:underline">
              삭제
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function FieldEditor({ field, sections, onChange, onDelete, onUploadImage, imageUploading = false }) {
  const patch = (key, value) => onChange({ [key]: value })
  const sectionOptions = Array.isArray(sections) ? sections : []
  const isImageField = String(field.type || '').toLowerCase() === 'image'
  const isDynamicUserPhotoField = DYNAMIC_USER_PHOTO_BIND_KEYS.has(String(field.bindKey || ''))
  const patchSection = (sectionKey) => {
    const section = sectionOptions.find((item) => item.key === sectionKey)
    onChange({ sectionKey, sectionLabel: section?.label || '' })
  }
  const patchPreset = (bindKey) => {
    const preset = FIELD_PRESETS.find((item) => item.bindKey === bindKey)
    onChange({
      bindKey,
      type: preset?.type || 'text',
      valueType: preset?.valueType || '',
      value: preset?.value != null ? preset.value : '',
      align: preset?.align || field.align || 'left',
      fontSize: preset?.fontSize || field.fontSize || 12,
    })
  }
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-xs font-semibold text-slate-700">내용</span>
        <select
          className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
          value={field.bindKey || ''}
          onChange={(e) => patchPreset(e.target.value)}
        >
          {FIELD_PRESETS.map((preset) => (
            <option key={preset.bindKey} value={preset.bindKey}>{preset.label}</option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-xs font-semibold text-slate-700">출력 섹션</span>
        <select
          className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
          value={field.sectionKey || ''}
          onChange={(e) => patchSection(e.target.value)}
        >
          <option value="">공통</option>
          {sectionOptions.map((section) => (
            <option key={section.key} value={section.key}>{section.label || section.key}</option>
          ))}
        </select>
      </label>

      {isImageField && isDynamicUserPhotoField && (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          로그인 설계사의 배경 제거 사진이 자동 매핑됩니다.
        </div>
      )}

      {isImageField && !isDynamicUserPhotoField && (
        <div className="space-y-2">
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">이미지 파일 ID</span>
            <input
              className="mt-1 w-full rounded border border-slate-200 px-3 py-2 font-mono text-xs outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
              value={field.value || ''}
              onChange={(e) => onChange({ value: e.target.value, valueType: 'STATIC', type: 'image' })}
              placeholder="이미지를 업로드하면 자동 입력"
            />
          </label>
          <label className="inline-flex cursor-pointer items-center justify-center rounded border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            {imageUploading ? '업로드 중' : '이미지 업로드'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={imageUploading}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) onUploadImage && onUploadImage(file)
                e.target.value = ''
              }}
            />
          </label>
        </div>
      )}

      {!isImageField && String(field.valueType || '').toUpperCase() === 'STATIC' && (
        <label className="block">
          <span className="text-xs font-semibold text-slate-700">직접 입력 문구</span>
          <textarea
            className="mt-1 min-h-[76px] w-full rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
            value={field.value || ''}
            onChange={(e) => patch('value', e.target.value)}
          />
        </label>
      )}

      {!isImageField && <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs font-semibold text-slate-700">글자 크기</span>
          <input
            type="number"
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
            value={field.fontSize ?? 12}
            onChange={(e) => patch('fontSize', Number(e.target.value || 12))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-700">정렬</span>
          <select
            className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
            value={field.align || 'left'}
            onChange={(e) => patch('align', e.target.value)}
          >
            <option value="left">왼쪽</option>
            <option value="center">가운데</option>
            <option value="right">오른쪽</option>
          </select>
        </label>
      </div>}

      {!isImageField && <label className="block">
        <span className="text-xs font-semibold text-slate-700">글꼴</span>
        <select
          className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm"
          value={field.fontKey || ''}
          onChange={(e) => patch('fontKey', e.target.value)}
        >
          {PDF_MAPPING_FONT_OPTIONS.map((option) => (
            <option key={option.value || 'default'} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>}

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs font-semibold text-slate-700">글자색</span>
          <input
            type="color"
            className="mt-1 h-10 w-full rounded border border-slate-200"
            value={field.textColor || '#111827'}
            onChange={(e) => patch('textColor', e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-700">배경색</span>
          <input
            type="color"
            className="mt-1 h-10 w-full rounded border border-slate-200"
            value={field.backgroundColor || '#ffffff'}
            onChange={(e) => patch('backgroundColor', e.target.value)}
          />
        </label>
      </div>

      <div className="space-y-2 text-sm text-slate-700">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(field.coverBackground)}
            onChange={(e) => patch('coverBackground', e.target.checked)}
          />
          기존 글자 가리기
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={field.fitFont !== false}
            onChange={(e) => patch('fitFont', e.target.checked)}
          />
          박스에 맞춰 글자 크기 조정
        </label>
      </div>

      <button
        type="button"
        onClick={onDelete}
        className="w-full rounded border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
      >
        필드 삭제
      </button>
    </div>
  )
}
